import type { DataTableRowClickEvent, DataTableRowDoubleClickEvent } from 'primevue';
import type { VNode } from 'vue';

import type { PartialConfirmMessage } from '../confirm/Confirm.vue';

import type { PrefilterTab } from './DataTablePrefilterTabs.vue';
import type { AssignableTemplateNames } from './dataTableTemplateCache';

export type align = 'left' | 'center' | 'right';

export type RowRouteAction = 'view' | 'edit';

/**
 * Wrapper class to store a value along with arbitrary metadata.
 */
export class DataWithMeta<T = any> {
  private _meta: Record<string, any> = {};

  constructor(public value: T) {}

  meta(): Record<string, any>;
  meta(key: string): any;
  meta(key: string, value: any): void;
  meta(key?: string, value?: any): any {
    if (key !== undefined && value !== undefined) {
      this._meta[key] = value;
    } else if (key !== undefined) {
      return this._meta[key];
    } else {
      return this._meta;
    }
  }
}

/**
 * Object that maps each key to a DataWithMeta wrapper for metadata support.
 */
export interface EnrichedDataWithMeta<T extends Record<string, any>> {
  [key: string]: DataWithMeta<T[keyof T]>;
}

/**
 * A single row object with metadata attached to each field.
 * Each field is wrapped in DataWithMeta for access to raw values and metadata.
 */
export type MetaEnrichedRow<T extends Record<string, any>> = {
  [K in keyof T]: DataWithMeta<T[K]>;
};

/**
 * Legacy alias for MetaEnrichedRow - kept for backward compatibility
 */
export type MetaData<T extends Record<string, any>> = MetaEnrichedRow<T>;

export interface ColumnFormatterContext {
  locale: string;
  row?: Record<string, any>;
  column: ColumnConfig;
}

export type ColumnFormatter = (rawValue: string, ctx: ColumnFormatterContext) => string;

export type DataTableSurfaceContext = 'ground' | 'card' | 'section';

export type EnumOption = {
  value: string | number;
  label: string;
  disabled?: boolean;
};

/**
 * Defines a column configuration for dynamic DataTable components.
 */
export interface ColumnConfig {
  /** The field name in the data object that this column maps to */
  field: string;

  /** Optional column header for display in the table UI */
  header?: string;

  /** Alternative to `header` for internal identification or additional UI display */
  title?: string;

  /** Indicates if the column is sortable */
  sortable?: boolean;

  /** Enables filter input for this column */
  filter?: boolean;

  /** Enables inline editing for this column in the DataTable wrapper */
  editable?: boolean;

  /**
   * Defines the data type of the column for proper rendering and filtering
   * Supported types: 'string', 'number', 'boolean', 'enum', 'date', 'array'
   */
  type?:
    | 'string'
    | 'number'
    | 'boolean'
    | 'enum'
    | 'date'
    | 'datetime'
    | 'time'
    | 'array'
    | 'stringArray'
    | 'dateArray'
    | 'objectArray'
    | 'enumArray'
    | 'object';

  /**
   * If `type` is 'object' or 'array of objects', specifies the key to extract
   * for search or display purposes (e.g., 'label' from { label: 'X', value: 'Y' })
   */
  searchKey?: string;

  /** If true, the column will be initially hidden but toggleable */
  hideInitial?: boolean;

  /** on enums the order of the keys can be set in case of sorting the table */
  sortOrder?: string[];

  /** If true, values can be clustered by sorting if we assume that the values are limited in amount so clustering makes sense */
  clusterable?: boolean;

  /** Controls current visibility of the column */
  hidden?: boolean;

  /** Alignment of cell content: 'left', 'center', or 'right' */
  align?: align;

  /** Maximum text length before truncation (with ellipsis) */
  truncateLength?: number;

  /** Combines multiple fields into one display column */
  combinedField?: string[];

  /** Name of a template from cache from a set of available comon cell templates */
  template?: AssignableTemplateNames;

  /** If true, prevents text wrapping in the cell */
  noWrap?: boolean;

  /** Icon for boolean `true` values */
  iconTrue?: string;

  /** Icon for boolean `false` values */
  iconFalse?: string;

  /** Custom cell renderer. Can return raw HTML or a Vue VNode */
  renderCell?: (value: any, slotProps?: any) => VNode | string;

  /** Function to transform a string (e.g. IBAN, phone) into a formatted string */
  formatter?: ColumnFormatter;

  /**
   * Enum editing options. If provided and type==='enum', DataTableCell can render a Select in edit mode.
   */
  enumOptions?: EnumOption[] | ((ctx: { row: any; col: ColumnConfig }) => EnumOption[]);

  /**
   * Optional: prefix for i18n keys, if you want to derive labels from value automatically.
   * Example: 'enums.CreateObjectionPositionDto.status'
   */
  enumI18nKeyPrefix?: string;
}

export interface RowGroupConfig {
  /** PrimeVue rowGroupMode, currently we only support 'subheader' */
  mode?: 'subheader';
  /** Field name used to group rows (e.g. 'invoiceId') */
  groupBy: string;
  /**
   * Optional list of sort fields that activate grouping.
   * If omitted or empty, grouping is always enabled.
   * Example: ['invoice', 'invoiceId']
   */
  activateOnSortFields?: string[];
}

export interface CellEditPayload {
  /** Raw ID der Zeile (z. B. aus row.id.meta('rawvalue')) */
  rowId: string;
  /** Field name of the column */
  field: string;
  /** New value */
  newValue: any;
  /** Old value, from before the edit */
  oldValue: any;
  /** Unwrapped without DataWithMeta (DTO-Form) */
  row: Record<string, any>;
}

export type RowRouteIdResolver = string | ((row: any) => string | number | null | undefined);

/**
 * Props accepted by the reusable DataTable component.
 */
export interface DataTableProps {
  /** Dataset to be displayed in the table */
  data: Record<string, any>[];

  /** Configuration for each column */
  columns: ColumnConfig[];

  /** Optional callback triggered when delete is invoked */
  onDelete?: (selectedRowIds: any[], key?: string) => void;

  /** Optional callback triggered when save is invoked */
  onSave?: () => void | Promise<void>;

  /** Optional callback triggered when create is invoked */
  onCreate?: () => void;

  /** Unique table key for relation tables to distinguish them in case of order change */
  dataId?: string;

  /** Unique table key for storing user settings like column order/filter/sort */
  dataKey?: string;

  /** Enables routing logic for tables within subcomponents */
  isSubcomponent?: boolean;

  /** Whether to show the top-level toolbar (e.g., "Add", pagination, etc.) */
  showPrimaryToolbar?: boolean;

  /** Shows default actions per row (e.g., edit, view, delete) */
  showRowActions?: boolean;

  /** Shows refresh button */
  showRefresh?: boolean;

  /** Shows delete button */
  showDelete?: boolean;

  /** Shows delete button */
  showSplitView?: boolean;

  /** Calls on refresh buttons action */
  onRefresh?: () => void;

  /** Callback on single row click */
  onRowClick?: (event: DataTableRowClickEvent) => void;

  /** Callback on row double-click */
  onRowDblClick?: (event: DataTableRowDoubleClickEvent) => void;

  /** Optional class for the `<DataTable>` root element */
  tableClass?: string;

  /** Optional surface context for gap/background alignment (e.g. 'ground' for page, 'card' for inside Card) */
  tableSurfaceContext?: DataTableSurfaceContext;

  /** Function to compute a CSS class for each row based on its content */
  rowClass?: (data: any) => string;

  /** Configuration for grouping rows */
  rowGroupConfig?: RowGroupConfig;

  /** Allows injection of custom sort logic */
  applySort?: (data: Record<string, any>[]) => void;

  /** Row selection behavior. Defaults to `'multiple'` */
  selectionMode?: 'single' | 'multiple';

  /** Allows user to remove sorting from a column */
  removableSort?: boolean;

  /** If false, displays a loading state or message (used for async tables) */
  isLoadingFinished?: boolean;

  /** Enables row drag-and-drop for manual sorting */
  reorderableRows?: boolean;

  /** Enables row drag-and-drop for manual sorting of columns */
  reorderableColumns?: boolean;

  /** Shows context menu on right click */
  tableActions?: boolean;

  /** Displays the toolbar buttons (e.g., reorder, settings) */
  showSecondaryToolbarButtons?: boolean;

  /** Shows the second toolbar (filters, settings, etc.) */
  showSecondaryToolbar?: boolean;

  /** Enables expandable rows (e.g., to preview more content below the row) */
  hasExpandedRow?: boolean;

  /** Whether the expanded row is collapsed by default */
  collapsedExpandedRow?: boolean;

  /** Enables or disables row selection via checkboxes which is by default configurable by the user */
  checkboxMultiselect?: boolean;

  /** Enables prefilter tabs when a configuration object is given */
  prefilterTabs?: PrefilterTab[];

  /** Enables sorting */
  sortable?: boolean;

  /** Enables filtering */
  filter?: boolean;

  /** Enables initial row selection (meta-enriched rows) */
  selectedRows?: MetaEnrichedRow<Record<string, any>>[];

  /** Global edit mode flag – if true, enables editing features */
  editMode?: boolean;

  /** Callback, triggered when a cell is edited */
  onCellEdit?: (payload: CellEditEvent) => void;

  /** If column count exceeds this threshold, short labels are used */
  shortLabelsThreshold?: number;

  /** Resolver for row route IDs, either a string or a function returning a string/number */
  rowRouteId?: RowRouteIdResolver;

  confirmationMessage?: (ids: (string | number)[]) => Partial<PartialConfirmMessage>;
}

/**
 * Simpler version of the table props used for internal use.
 */
export interface SimpleTableProps {
  data: Record<string, any>[];
  columns: ColumnConfig[];
}

/**
 * ConfigTableProps allows for all DataTableProps except data/columns (those are replaced).
 */
export type ConfigTableProps = Omit<DataTableProps, keyof SimpleTableProps>;

/**
 * Utility interface used when row and column are passed together in slots or event handlers.
 */
export interface RowAndColum {
  row: any;
  column: ColumnConfig;
}

/**
 * Interface used in expanded row slots.
 */
export interface ExpandedTableRow {
  data: any;
}

/**
 * rowclick events on dataTable-Row
 */
export type LineRowClickEvent = {
  data: { id: { _meta: { rawvalue: string } } };
  originalEvent?: MouseEvent;
};

export type CellEditSingle = {
  mode: 'single';
  rowId: string;
  field: string;
  oldValue: any;
  newValue: any;
  row: Record<string, any>;
};

export type CellEditBulkItem = {
  rowId: string;
  oldValue: any;
  row: Record<string, any>;
};

export type CellEditBulk = {
  mode: 'bulk';
  field: string;
  newValue: any;
  edits: CellEditBulkItem[];
};

export type CellEditEvent = CellEditSingle | CellEditBulk;

export type TabDirection = 'forward' | 'backward' | 'up' | 'down';
export interface TabRequestPayload {
  rowId: string | number;
  field: string;
  direction: TabDirection;
  additive?: boolean;
  range?: boolean;
  focusOnly?: boolean;
}

export type SelectionIntent = 'replace' | 'add' | 'range' | 'ensure' | 'none';

/**
 * Helper to generate column configs from raw field names
 */
export const generateColumnConfigs = (columns: string[]): ColumnConfig[] => {
  return columns.map((field) => ({
    field,
    header: field.charAt(0).toUpperCase() + field.slice(1),
    sortable: true,
    filter: true,
    type: 'string',
  }));
};

export type ColumnType =
  | 'string'
  | 'number'
  | 'date'
  | 'enum'
  | 'boolean'
  | 'array'
  | 'stringArray'
  | 'dateArray'
  | 'objectArray';
